import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

// En desarrollo el backend corre en localhost:5000; en producción el socket se
// conecta al mismo origen donde se sirve la app.
const SOCKET_BASE = process.env.NODE_ENV === 'production'
  ? window.location.origin
  : 'http://localhost:5000';

const PAGE_SIZE = 20;

const STATUS_OPTS = ['requested', 'dispatched', 'delivered', 'cancelled'];
const STATUS_ES = { requested: 'Solicitado', dispatched: 'Despachado', delivered: 'Entregado', cancelled: 'Cancelado' };

const PAY_OPTS = ['open', 'APPROVED', 'COD', 'DECLINED', 'ERROR'];
const PAY_ES = { open: 'Pendiente', APPROVED: 'Aprobado', COD: 'Contraentrega', DECLINED: 'Rechazado', ERROR: 'Error' };
const PAY_COLOR = { open: '#9ca3af', APPROVED: '#22c55e', COD: '#f59e0b', DECLINED: '#ef4444', ERROR: '#ef4444' };

const ROW_BG = {
  requested: 'transparent',
  dispatched: 'rgba(59,130,246,0.12)',
  delivered: 'rgba(34,197,94,0.12)',
  cancelled: 'rgba(239,68,68,0.12)',
};

const fmt = (v) => v == null ? '—' : `$${parseFloat(v).toLocaleString('es-CO')}`;

// Opciones al facturar un pedido: cada una abre el cajero en el modo elegido.
const FACTURAR_OPTS = [
  { action: 'facturar', label: 'Factura', icon: '🧾' },
  { action: 'facturar_pos', label: 'Factura POS', icon: '🛒' },
  { action: 'cotizar', label: 'Cotización', icon: '📝' },
];

function Pedidos({ token }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [updatingId, setUpdatingId] = useState(null);
  const [itemsModal, setItemsModal] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);

  // Facturación de un pedido: modal para elegir tipo de documento y estado de
  // "preparando" mientras se traen los ítems antes de navegar al cajero.
  const [facturarBill, setFacturarBill] = useState(null);
  const [preparing, setPreparing] = useState(null);

  // Exportar pedidos a CSV por rango de fechas.
  const [showExport, setShowExport] = useState(false);
  const [exportForm, setExportForm] = useState({ from: '', to: '' });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const authH = { Authorization: `Bearer ${token}` };

  const fetchData = () => {
    setLoading(true);
    fetch(`/api/bills?page=${page}`, { headers: authH })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [page, token]);

  // El socket se crea una sola vez (depende solo del token); este ref siempre
  // apunta al fetchData actual para no reconectar al cambiar de página.
  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;

  // Tiempo real: cuando entra un pedido nuevo o cambia su estado en el backend
  // (tienda pública, bot/worker, otro agente o admin), el servidor emite
  // 'bill_changed' a la empresa y refrescamos la lista sin que el admin recargue.
  useEffect(() => {
    if (!token) return;
    const socket = io(`${SOCKET_BASE}/admin`, { auth: { token } });
    socket.on('bill_changed', () => fetchRef.current());
    return () => socket.disconnect();
  }, [token]);

  const changeStatus = async (id, status) => {
    setUpdatingId(id);
    await fetch(`/api/bills/${id}/status`, {
      method: 'PUT',
      headers: { ...authH, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    setData(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    setUpdatingId(null);
  };

  const changePay = async (id, pay) => {
    setUpdatingId(id);
    await fetch(`/api/bills/${id}/pay`, {
      method: 'PUT',
      headers: { ...authH, 'Content-Type': 'application/json' },
      body: JSON.stringify({ pay }),
    });
    setData(prev => prev.map(b => b.id === id ? { ...b, pay } : b));
    setUpdatingId(null);
  };

  // ── Exportar a CSV ──
  const openExport = () => {
    const now = new Date();
    const p = (n) => String(n).padStart(2, '0');
    const first = `${now.getFullYear()}-${p(now.getMonth() + 1)}-01`;
    const last = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate())}`;
    setExportForm({ from: first, to: last });
    setExportError('');
    setShowExport(true);
  };

  const doExport = async () => {
    const { from, to } = exportForm;
    if (!from || !to) return setExportError('Selecciona ambas fechas');
    if (from > to) return setExportError('La fecha inicial no puede ser mayor que la final');
    setExporting(true); setExportError('');
    try {
      const q = new URLSearchParams({ from, to });
      const r = await fetch(`/api/bills/export?${q}`, { headers: authH });
      if (!r.ok) {
        let msg = 'No se pudo exportar';
        try { const j = await r.json(); msg = j.message || msg; } catch { /* respuesta no JSON */ }
        setExportError(msg);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedidos_${from}_a_${to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setShowExport(false);
    } catch {
      setExportError('No se pudo exportar');
    } finally {
      setExporting(false);
    }
  };

  const openItems = async (billId) => {
    setItemsModal({ billId, items: [] });
    setLoadingItems(true);
    const r = await fetch(`/api/bills/${billId}/items`, { headers: authH });
    const json = r.ok ? await r.json() : { data: [] };
    setItemsModal({ billId, items: json.data || [] });
    setLoadingItems(false);
  };

  // Abre el cajero en el modo elegido, prellenado con los datos del cliente y los
  // ítems del pedido. Al crear el documento allí, el pedido queda ligado (tabla
  // intermedia) y este botón pasará a ser "Imprimir".
  const startFacturar = async (bill, action) => {
    setFacturarBill(null);
    setPreparing(bill.id);
    let items = [];
    try {
      const r = await fetch(`/api/bills/${bill.id}/items`, { headers: authH });
      const json = r.ok ? await r.json() : { data: [] };
      items = (json.data || []).map(it => ({
        ref: it.product_id || '', name: it.name || '', description: it.description || '',
        quantity: it.quantity, unit_price: it.price,
      }));
    } catch { /* si falla, se abre igual con los datos del cliente */ }
    // El domicilio es un campo del pedido, no un ítem: se agrega como línea aparte
    // para que también se cobre en la factura.
    if (bill.domicile_value && parseFloat(bill.domicile_value) > 0) {
      items.push({
        ref: '', name: 'Domicilio', description: '',
        quantity: 1, unit_price: bill.domicile_value,
      });
    }
    setPreparing(null);
    navigate('/cajero', {
      state: {
        cajeroPrefill: {
          action, billId: bill.id,
          customer: {
            name: bill.customer_name || '', address: bill.address || '',
            phone: bill.phone || '', email: bill.customer_email || '',
          },
          items,
        },
      },
    });
  };

  // Imprime el documento del cajero ligado al pedido (una vez facturado).
  const printInvoice = async (bill) => {
    setPreparing(bill.id);
    try {
      const r = await fetch(`/api/cajero/docs/${bill.invoice_doc_type}/${bill.invoice_doc_id}/print`, { headers: authH });
      const data = await r.json();
      if (r.ok && data.url) window.open(data.url, '_blank');
    } catch { /* noop */ }
    setPreparing(null);
  };

  return (
    <div style={{ padding: isMobile ? '1.2rem 0.8rem' : '2rem', maxWidth: '1300px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <h2 style={{ color: theme.colors.primary, margin: 0 }}>Pedidos</h2>
        <button onClick={openExport} style={{
          background: 'transparent', color: theme.colors.text,
          border: `1px solid ${theme.colors.border}`,
          padding: '7px 16px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
        }}>⬇ Exportar CSV</button>
      </div>

      {loading ? (
        <p style={{ color: theme.colors.textMuted }}>Cargando...</p>
      ) : data.length === 0 ? (
        <p style={{ color: theme.colors.textMuted }}>No hay pedidos registrados.</p>
      ) : (
        <>
          {/* Leyenda de colores */}
          <div style={{ display: 'flex', gap: '1.2rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {[
              { color: 'transparent', border: `1px solid ${theme.colors.border}`, label: 'Solicitado' },
              { color: 'rgba(59,130,246,0.2)', border: '1px solid #3b82f6', label: 'Despachado' },
              { color: 'rgba(34,197,94,0.2)', border: '1px solid #22c55e', label: 'Entregado' },
              { color: 'rgba(239,68,68,0.2)', border: '1px solid #ef4444', label: 'Cancelado' },
            ].map(({ color, border, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: color, border }} />
                <span style={{ color: theme.colors.textMuted, fontSize: '0.75rem' }}>{label}</span>
              </div>
            ))}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                  {['Cliente', 'Teléfono', 'Ciudad', 'Subtotal', 'Domicilio', 'Total', 'Dirección', 'Estado', 'Pago', 'Fecha', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: theme.colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map(bill => (
                  <tr
                    key={bill.id}
                    style={{
                      backgroundColor: ROW_BG[bill.status] || 'transparent',
                      borderBottom: `1px solid ${theme.colors.border}22`,
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <td style={{ padding: '8px 10px', color: theme.colors.text }}>{bill.customer_name || '—'}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text }}>{bill.phone}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted }}>{bill.city || '—'}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text }}>{fmt(bill.subtotal)}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text }}>{fmt(bill.domicile_value)}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text, fontWeight: 600 }}>{fmt(bill.total)}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {bill.address || '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <select
                        value={bill.status}
                        disabled={updatingId === bill.id}
                        onChange={e => changeStatus(bill.id, e.target.value)}
                        style={{
                          backgroundColor: theme.colors.background,
                          border: `1px solid ${theme.colors.border}`,
                          color: bill.status === 'delivered' ? '#22c55e'
                            : bill.status === 'dispatched' ? '#60a5fa'
                            : bill.status === 'cancelled' ? '#ef4444'
                            : theme.colors.text,
                          borderRadius: '5px', padding: '3px 6px',
                          fontSize: '0.8rem', cursor: 'pointer', outline: 'none',
                        }}
                      >
                        {STATUS_OPTS.map(s => (
                          <option key={s} value={s}>{STATUS_ES[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <select
                        value={PAY_OPTS.includes(bill.pay) ? bill.pay : 'open'}
                        disabled={updatingId === bill.id}
                        onChange={e => changePay(bill.id, e.target.value)}
                        style={{
                          backgroundColor: theme.colors.background,
                          border: `1px solid ${theme.colors.border}`,
                          color: PAY_COLOR[bill.pay] || theme.colors.text,
                          borderRadius: '5px', padding: '3px 6px',
                          fontSize: '0.8rem', cursor: 'pointer', outline: 'none',
                        }}
                      >
                        {PAY_OPTS.map(p => (
                          <option key={p} value={p}>{PAY_ES[p]}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {bill.created_at
                        ? new Date(bill.created_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })
                        : '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button
                          onClick={() => openItems(bill.id)}
                          style={{
                            background: 'none', border: `1px solid ${theme.colors.border}`,
                            color: theme.colors.text, padding: '3px 12px',
                            borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem',
                          }}
                        >
                          Ver
                        </button>
                        {bill.wa_number && (
                          <button
                            onClick={() => window.open(`https://wa.me/${bill.wa_number}`, '_blank', 'noopener,noreferrer')}
                            title="Contactar por WhatsApp"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              background: '#25D366', border: 'none', color: '#fff',
                              padding: '3px 12px', borderRadius: '5px', cursor: 'pointer',
                              fontSize: '0.78rem', whiteSpace: 'nowrap', fontWeight: 600,
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.821 11.821 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l-.999 3.648 3.733-.979a9.872 9.872 0 0 0 .245.172zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                            </svg>
                            Contactar
                          </button>
                        )}
                        {bill.invoice_doc_type ? (
                          <button
                            onClick={() => printInvoice(bill)}
                            disabled={preparing === bill.id}
                            title={`Imprimir ${bill.invoice_full_number || ''}`}
                            style={{
                              background: theme.colors.primary, border: 'none', color: '#000',
                              padding: '3px 12px', borderRadius: '5px', cursor: 'pointer',
                              fontSize: '0.78rem', whiteSpace: 'nowrap', fontWeight: 600,
                              opacity: preparing === bill.id ? 0.6 : 1,
                            }}
                          >
                            🖨 Imprimir
                          </button>
                        ) : (
                          <button
                            onClick={() => setFacturarBill(bill)}
                            disabled={preparing === bill.id}
                            style={{
                              background: 'none', border: `1px solid ${theme.colors.primary}`,
                              color: theme.colors.primary, padding: '3px 12px',
                              borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem',
                              whiteSpace: 'nowrap', fontWeight: 600,
                              opacity: preparing === bill.id ? 0.6 : 1,
                            }}
                          >
                            {preparing === bill.id ? '…' : 'Facturar'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem', alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: '5px', border: `1px solid ${theme.colors.border}`, background: 'none', color: theme.colors.text, cursor: 'pointer' }}>‹</button>
              <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Página {page} de {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '5px 12px', borderRadius: '5px', border: `1px solid ${theme.colors.border}`, background: 'none', color: theme.colors.text, cursor: 'pointer' }}>›</button>
            </div>
          )}
        </>
      )}

      {/* Modal: elegir tipo de documento al facturar un pedido */}
      {facturarBill && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setFacturarBill(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: theme.colors.surface, borderRadius: '12px', padding: isMobile ? '1.4rem' : '2rem',
            width: '100%', maxWidth: '420px', border: `1px solid ${theme.colors.border}`, boxSizing: 'border-box',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
              <h3 style={{ color: theme.colors.primary, margin: 0 }}>Facturar pedido</h3>
              <button onClick={() => setFacturarBill(null)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.84rem', margin: '0 0 1.2rem' }}>
              ¿Qué documento quieres generar para <strong style={{ color: theme.colors.text }}>{facturarBill.customer_name || facturarBill.phone}</strong>? Se abrirá el cajero con los datos del cliente y los productos ya cargados.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {FACTURAR_OPTS.map(opt => (
                <button key={opt.action} onClick={() => startFacturar(facturarBill, opt.action)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left',
                    background: theme.colors.background, border: `1px solid ${theme.colors.border}`,
                    color: theme.colors.text, padding: '12px 14px', borderRadius: '9px', cursor: 'pointer',
                    fontSize: '0.92rem', fontWeight: 600, fontFamily: 'inherit',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = theme.colors.primary)}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = theme.colors.border)}
                >
                  <span style={{ fontSize: '1.3rem' }}>{opt.icon}</span> {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal detalle de productos */}
      {itemsModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '1rem' : 0 }}>
          <div style={{
            backgroundColor: theme.colors.surface, borderRadius: '12px', padding: isMobile ? '1.2rem' : '2rem',
            width: '100%', maxWidth: '720px', border: `1px solid ${theme.colors.border}`, boxSizing: 'border-box',
            maxHeight: isMobile ? '85vh' : '80vh', overflow: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <h3 style={{ color: theme.colors.primary, margin: 0 }}>Detalle del pedido</h3>
              <button onClick={() => setItemsModal(null)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            {loadingItems ? (
              <p style={{ color: theme.colors.textMuted }}>Cargando...</p>
            ) : itemsModal.items.length === 0 ? (
              <p style={{ color: theme.colors.textMuted }}>Sin productos registrados.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                    {['ID Producto', 'Nombre', 'Descripción', 'Cantidad', 'Precio', 'Subtotal'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: theme.colors.textMuted, fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {itemsModal.items.map(item => (
                    <tr key={item.id} style={{ borderBottom: `1px solid ${theme.colors.border}22` }}>
                      <td style={{ padding: '7px 10px', color: theme.colors.textMuted, fontFamily: 'monospace', fontSize: '0.78rem' }}>{item.product_id}</td>
                      <td style={{ padding: '7px 10px', color: theme.colors.text, fontWeight: 500 }}>{item.name || '—'}</td>
                      <td style={{ padding: '7px 10px', color: theme.colors.textMuted, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description || '—'}</td>
                      <td style={{ padding: '7px 10px', color: theme.colors.text, textAlign: 'center' }}>{item.quantity}</td>
                      <td style={{ padding: '7px 10px', color: theme.colors.text }}>{fmt(item.price)}</td>
                      <td style={{ padding: '7px 10px', color: theme.colors.text, fontWeight: 600 }}>
                        {fmt(parseFloat(item.price) * parseInt(item.quantity))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modal de exportación */}
      {showExport && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => !exporting && setShowExport(false)}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: theme.colors.surface, borderRadius: '12px', padding: isMobile ? '1.4rem' : '2rem',
              width: '100%', maxWidth: '430px', border: `1px solid ${theme.colors.border}`, boxSizing: 'border-box',
            }}
          >
            <h3 style={{ color: theme.colors.primary, margin: '0 0 0.4rem' }}>Exportar pedidos</h3>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.84rem', margin: '0 0 1.3rem' }}>
              Selecciona el rango de fechas. Se descargará un archivo CSV con la información de los pedidos.
            </p>
            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 130 }}>
                <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.82rem', marginBottom: '4px' }}>Desde</label>
                <input type="date" value={exportForm.from} onChange={e => setExportForm(f => ({ ...f, from: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '6px', color: theme.colors.text, fontSize: '0.85rem', boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1, minWidth: 130 }}>
                <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.82rem', marginBottom: '4px' }}>Hasta</label>
                <input type="date" value={exportForm.to} onChange={e => setExportForm(f => ({ ...f, to: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '6px', color: theme.colors.text, fontSize: '0.85rem', boxSizing: 'border-box' }} />
              </div>
            </div>
            {exportError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: '0.9rem 0 0' }}>{exportError}</p>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button onClick={() => setShowExport(false)} disabled={exporting} style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '7px 16px', borderRadius: '7px', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={doExport} disabled={exporting} style={{ backgroundColor: theme.colors.primary, color: '#000', border: 'none', padding: '7px 18px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600 }}>
                {exporting ? 'Exportando...' : 'Exportar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Pedidos;
